import * as React from 'react';
import { useParams } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import RangeDatePicker from '../../components/Layout/DatePicker.tsx'; // Likely needed for top date filter
// Import from the new central data location
import { partnersData } from '../../data/partnersData.ts';
// Import risk management data to access partners on hold and high risk partners
import { mockOnHoldPartners, mockHighRiskPartners } from '../RiskManagementPage/RiskManagementPage.tsx';
import { 
  ArrowLeft, Edit, Download, MoreHorizontal, 
  MapPin, Link as LinkIcon, FileText, UserPlus, 
  Eye, CheckCircle, AlertCircle, Search, Filter,
  Phone, Mail, Calendar, ChevronDown,
  Hash, Copy, Check, Send, Users, ChevronRight,
  Clock, BarChart2, Calendar as CalendarIcon, CheckCircle as CheckCircleIcon,
  ChevronLeft as PaginationChevronLeft, ChevronRight as PaginationChevronRight // Added Pagination Chevrons
} from 'react-feather'; // Added Hash, Copy, Check, Send, Users, ChevronRight, Clock, BarChart2, Calendar, CheckCircle

// Interface for Graph Data Point
interface GraphDataPoint {
    name: string;
    value: number;
}

// Placeholder for Balance Graph
const BalanceGraph: React.FC<{ data?: GraphDataPoint[], currency?: string }> = ({ data, currency }) => {
    const graphHeight = 80;
    const graphWidth = 300;
    const svgRef = React.useRef<SVGSVGElement>(null);
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
    const [hoverPosition, setHoverPosition] = React.useState<{ x: number; y: number } | null>(null);

    if (!data || data.length === 0) {
        return (
            <div style={{ marginTop: '20px', height: `${graphHeight}px`, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: '13px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                    No data available for graph
                </div>
            </div>
        );
    }

    const values = data.map((d) => d.value);
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const range = maxValue - minValue <= 0 ? 1 : maxValue - minValue;

    const calculateY = (value: number) => graphHeight - ((value - minValue) / range) * graphHeight;

    const getControlPoints = (p0: {x: number, y: number}, p1: {x: number, y: number}, p2: {x: number, y: number}, tension = 0.3) => {
        const d01 = Math.sqrt(Math.pow(p1.x - p0.x, 2) + Math.pow(p1.y - p0.y, 2));
        const d12 = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

        const fa = tension * d01 / (d01 + d12 || 1);
        const fb = tension * d12 / (d01 + d12 || 1);

        const cp1x = p1.x - fa * (p2.x - p0.x);
        const cp1y = p1.y - fa * (p2.y - p0.y);
        const cp2x = p1.x + fb * (p2.x - p0.x);
        const cp2y = p1.y + fb * (p2.y - p0.y);

        return { cp1: { x: cp1x, y: cp1y }, cp2: { x: cp2x, y: cp2y } };
    };

    const generatePath = (area = false) => {
        if (!data || data.length < 2) return '';

        const points = data.map((d, i) => ({
            x: (i / (data.length - 1)) * graphWidth,
            y: calculateY(d.value)
        }));

        let path = `M ${points[0].x},${points[0].y}`;

        for (let i = 0; i < points.length - 1; i++) {
            const p0 = i === 0 ? points[i] : points[i - 1];
            const p1 = points[i];
            const p2 = points[i + 1];
            const p3 = i === points.length - 2 ? points[i + 1] : points[i + 2];

            const controls1 = getControlPoints(p0, p1, p2);
            const controls2 = getControlPoints(p1, p2, p3);

            path += ` C ${controls1.cp2.x},${controls1.cp2.y} ${controls2.cp1.x},${controls2.cp1.y} ${p2.x},${p2.y}`;
        }

        if (area) {
            path += ` L ${graphWidth},${graphHeight} L 0,${graphHeight} Z`;
        }
        return path;
    };

    const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
        if (!svgRef.current || data.length < 1) return;

        const svgRect = svgRef.current.getBoundingClientRect();
        const mouseX = event.clientX - svgRect.left;
        const index = Math.max(0, Math.min(data.length - 1, Math.round((mouseX / graphWidth) * (data.length - 1))));
        const pointX = (index / (data.length - 1)) * graphWidth;
        const pointY = calculateY(data[index].value);
        
        setHoveredIndex(index);
        setHoverPosition({ x: pointX, y: pointY });
    };

    const handleMouseLeave = () => {
        setHoveredIndex(null);
        setHoverPosition(null);
    };

    const lineColor = '#059669';
    const areaGradientId = 'areaGradient';
    const gradientColor = '#C0F0CF';
    const pointRadius = 4;
    const pointStrokeWidth = 1.5;
    const tooltipOffsetY = 12;
    const tooltipFontSize = 11;

    const hoveredValue = hoveredIndex !== null ? data[hoveredIndex].value : null;
    const formattedHoveredValue = hoveredValue !== null ? `${currency || '$'} ${hoveredValue.toLocaleString()}` : '';

    return (
        <div style={{ marginTop: '20px', height: `${graphHeight}px`, position: 'relative' }}>
            <svg
                ref={svgRef}
                width="100%"
                height={graphHeight}
                viewBox={`0 0 ${graphWidth} ${graphHeight}`}
                preserveAspectRatio="none"
                style={{ display: 'block', overflow: 'visible' }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                <defs>
                    <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={gradientColor} stopOpacity={0.4}/>
                        <stop offset="100%" stopColor={gradientColor} stopOpacity={0.05}/>
                    </linearGradient>
                </defs>
                <path d={generatePath(true)} fill={`url(#${areaGradientId})`} />
                <path d={generatePath(false)} fill="none" stroke={lineColor} strokeWidth="2" />

                {hoverPosition && hoveredIndex !== null && hoveredValue !== null && (
                    <g>
                        <line
                            x1={hoverPosition.x} y1={0}
                            x2={hoverPosition.x} y2={graphHeight}
                            stroke="#9ca3af" strokeWidth="1" strokeDasharray="3,3"
                        />
                        <circle
                            cx={hoverPosition.x} cy={hoverPosition.y} r={pointRadius}
                            fill={lineColor} stroke="#ffffff" strokeWidth={pointStrokeWidth}
                        />
                        <text
                            x={hoverPosition.x}
                            y={hoverPosition.y - tooltipOffsetY}
                            fill="#111827"
                            fontSize={tooltipFontSize}
                            fontWeight="500"
                            textAnchor="middle"
                            style={{ pointerEvents: 'none' }}
                        >
                            {formattedHoveredValue}
                        </text>
                    </g>
                )}
            </svg>
            {data.length < 2 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: '13px', backgroundColor: '#f9fafb', borderRadius: '4px', position: 'absolute', top: 0, left: 0, width: '100%' }}>
                    Not enough data for graph
                </div>
            )}
        </div>
    );
};

// Component for the large Total Balance card with graph
interface TotalBalanceCardProps {
    title: string;
    amount: number;
    currency: string;
    trend?: number;
    graphData?: GraphDataPoint[];
}

const TotalBalanceCard: React.FC<TotalBalanceCardProps> = ({ title, amount, currency, trend, graphData }) => {
    const cardBgColor = '#ffffff';
    const cardBorderColor = '#f3f4f6';
    const boldShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'; // Bolder shadow
    const amountColor = '#111827';
    const textColor = '#374151';
    const lightTextColor = '#9ca3af';
    const greenColor = '#10b981';
    const redColor = '#f43f5e';
    
    const changeDirection = trend && trend > 0 ? '▲' : (trend && trend < 0 ? '▼' : '');
    const changeColor = changeDirection === '▲' ? greenColor : (changeDirection === '▼' ? redColor : lightTextColor);
    
    const formatAmount = (amount: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };
    
    const formattedAmount = formatAmount(amount);
    const formattedTrend = trend ? formatAmount(Math.abs(trend)) : '';

    const baseStyle: React.CSSProperties = {
        padding: '20px',
        borderRadius: '8px',
        backgroundColor: cardBgColor,
        boxShadow: boldShadow, // Updated to bolder shadow
        border: `1px solid ${cardBorderColor}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minWidth: '150px', // Further reduced minimum width
        flex: '1 1 150px'   // Adjusted flex properties (grow=1, shrink=1, basis=150px)
    };

    return (
        <div style={baseStyle}>
            <div>
                <div style={{ marginBottom: '4px', color: textColor, fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {title}
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" color={lightTextColor}>
                        <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm6.5-.25A.75.75 0 017.25 7h1.5a.75.75 0 010 1.5h-.75v2.5h.75a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75V8.25H6.5a.75.75 0 01-.75-.75zM8 5.5a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
                    </svg>
                </div>
                <div style={{ fontSize: '28px', fontWeight: '600', marginBottom: '2px', color: amountColor, lineHeight: '1.2' }}>{formattedAmount}</div>
                {trend && changeDirection && (
                    <span style={{ color: changeColor, display: 'flex', alignItems: 'center', gap: '3px', fontSize: '12px', fontWeight: '500' }}>
                        {changeDirection === '▲' && <svg width="10" height="10" fill={greenColor} viewBox="0 0 16 16"><path d="M7.247 4.86l-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z"/></svg>}
                        {changeDirection === '▼' && <svg width="10" height="10" fill={redColor} viewBox="0 0 16 16"><path d="M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/></svg>}
                        {`${changeDirection === '▲' ? '+' : '-'}${formattedTrend}`}
                    </span>
                )}
            </div>
            <BalanceGraph data={graphData} currency={currency} />
        </div>
    );
};

// --- Mock Data Structures ---
interface PartnerDetails {
    id: string;
    name: string;
    logoUrl: string | null;
    status: 'Active' | 'Inactive' | 'Pending';
    tier: string;
    location: string;
    joinedDate: string;
    products: string[];
    riskScore: number;
    riskStatus: 'On Hold' | 'High Risk' | 'Medium Risk' | 'Low Risk' | null; // Added risk status field
    debitBalance: number;
    debitTrend: number;
    creditBalance: number;
    creditTrend: number;
    netBalance: number;
    availableForPayout: number;
    payoutPending: number;
    payoutOnHold: number;
    currency: string;
    businessInfo: {
        address: string;
        website: string;
        taxId: string;
        registration: string;
    };
    contactPersons: ContactPerson[];
    riskAssessment: {
        score: number;
        tier: string;
        factors: RiskFactor[];
    };
    recentTransactions: Transaction[];
}

interface ContactPerson {
    id: string;
    name: string;
    role: string;
    email: string;
    phone: string;
    avatarUrl?: string | null;
}

interface RiskFactor {
    name: string;
    score: number;
    description: string;
}

interface Transaction {
    id: string; // Changed from reference to id
    type: string;
    product: string; // Changed from 'wallet' to 'product'
    amount: number;
    date: string; // ISO String
    status: 'Completed' | 'Pending' | 'Failed' | 'Refunded';
    category?: string;
}

// --- Mock Data ---
// Fallback mock partner data
const mockPartner: PartnerDetails = {
    id: 'PTR-20231001',
    name: 'Al Futtaim Group',
    logoUrl: 'https://companieslogo.com/img/orig/al-futtaim-logo.svg?t=1657177017', // Placeholder logo URL
    status: 'Active',
    tier: 'Platinum Tier',
    location: 'United Arab Emirates',
    joinedDate: '2021-03-15',
    products: ['BNPL', 'Acquiring', 'Capital'],
    riskScore: 92,
    riskStatus: 'Low Risk',
    debitBalance: 1250000,
    debitTrend: 8.5,
    creditBalance: 350000,
    creditTrend: -2.3,
    netBalance: 900000,
    availableForPayout: 750000,
    payoutPending: 120000,
    payoutOnHold: 30000,
    currency: 'AED',
    businessInfo: {
        address: 'Dubai Silicon Oasis, Building A1\nDubai, United Arab Emirates 12345',
        website: 'www.alfuttaim.com',
        taxId: 'TAX123456789',
        registration: 'REG987654321',
    },
    contactPersons: [
        { id: 'c1', name: 'Ahmed Al Maktoum', role: 'Finance Director', email: 'ahmed.maktoum@alfuttaim.com', phone: '+971 50 123 4567', avatarUrl: 'https://randomuser.me/api/portraits/men/32.jpg' },
        { id: 'c2', name: 'Sarah Johnson', role: 'Operations Manager', email: 'sarah.johnson@alfuttaim.com', phone: '+971 50 765 4321', avatarUrl: 'https://randomuser.me/api/portraits/women/44.jpg' },
        { id: 'c3', name: 'Mohammed Ali', role: 'Technical Contact', email: 'mohammed.ali@alfuttaim.com', phone: '+971 50 555 1234', avatarUrl: null },
    ],
    riskAssessment: {
        score: 92,
        tier: 'Platinum Tier',
        factors: [
            { name: 'Payment History', score: 95, description: 'Excellent payment history with no late payments' },
            { name: 'Transaction Volume', score: 85, description: 'Consistent transaction volume with expected patterns' },
            { name: 'Customer Complaints', score: 5, description: 'Very low rate of customer complaints' }, // Note: Score seems low for a high rating, assuming 5/100 is good here based on bar
            { name: 'Compliance', score: 100, description: 'Full compliance with all regulatory requirements' },
        ],
    },
    recentTransactions: [
        { id: 'TX-20250430-001', type: 'VAT', product: 'Acquiring', amount: 451.00, date: '2025-04-30T10:45:00Z', status: 'Failed', category: 'Credit' },
        { id: 'TX-20250501-002', type: 'Refund', product: 'BNPL', amount: -566.00, date: '2025-05-01T09:24:00Z', status: 'Pending', category: 'Correction' },
        { id: 'TX-20250425-003', type: 'VAT on Fees', product: 'BNPL', amount: 324.00, date: '2025-04-25T11:46:00Z', status: 'Failed', category: 'Credit' },
        { id: 'TX-20250425-004', type: 'Refund', product: 'Acquiring', amount: -130.00, date: '2025-04-25T10:58:00Z', status: 'Failed', category: 'Debit' },
        { id: 'TX-20250502-005', type: 'Revenue corrections', product: 'BNPL', amount: 214.00, date: '2025-05-02T06:55:00Z', status: 'Pending', category: 'Credit' },
        { id: 'TX-20250501-006', type: 'VAT', product: 'Acquiring', amount: 490.00, date: '2025-05-01T13:48:00Z', status: 'Pending', category: 'Debit' },
        { id: 'TX-20250429-007', type: 'Refund', product: 'BNPL', amount: -292.00, date: '2025-04-29T10:28:00Z', status: 'Completed', category: 'Debit' },
        { id: 'TX-20250429-007', type: 'Refund', product: 'BNPL', amount: -292.00, date: '2025-04-29T10:28:00Z', status: 'Completed', category: 'Debit' },
        { id: 'TX-20250429-007', type: 'Refund', product: 'BNPL', amount: -292.00, date: '2025-04-29T10:28:00Z', status: 'Completed', category: 'Debit' }
    ],
};

// --- Helper Functions for Partner Data Conversion ---
// Generate a simple UUID v4
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Transform partner data from PartnersPage format to PartnerDetails format
const transformPartnerData = (partnerData: any): PartnerDetails => {
    // Check if the partner has a risk status first
    const riskStatus = partnerData.riskStatus || null;
    
    // Set risk score based on risk status (if present) or extract from data
    // IMPORTANT: Ensure risk score is consistent with risk status
    let riskScore = 0;
    if (riskStatus === 'On Hold') {
        // On Hold partners should have extremely low risk scores
        riskScore = Math.floor(Math.random() * 10) + 5; // 5-15% score
    } else if (riskStatus === 'High Risk') {
        // High Risk partners should have low risk scores
        riskScore = Math.floor(Math.random() * 15) + 30; // 30-45% score
    } else if (riskStatus === 'Medium Risk') {
        // Medium Risk partners should have moderate risk scores
        riskScore = Math.floor(Math.random() * 10) + 60; // 60-70% score
    } else if (riskStatus === 'Low Risk') {
        // Low Risk partners should have high risk scores
        riskScore = Math.floor(Math.random() * 10) + 85; // 85-95% score
    } else {
        // If no risk status specified, use the percentage from string or default
        riskScore = parseInt(partnerData.risk?.replace('%', '')) || 85;
        
        // Set risk status based on score if not already set
        if (!riskStatus) {
            if (riskScore < 50) partnerData.riskStatus = 'High Risk';
            else if (riskScore < 70) partnerData.riskStatus = 'Medium Risk';
            else partnerData.riskStatus = 'Low Risk';
        }
    }
    
    // Generate a random ID if not present
    const id = partnerData.id || `PTR-${Math.floor(10000000 + Math.random() * 90000000)}`;
    
    // --- START: Conversion Factor Logic (from PartnersPage.tsx) ---
    let factor = 1;
    const location = partnerData.location;
    switch (location) {
        case 'KSA': factor = 3.75; break; // SAR
        case 'Qatar': factor = 3.64; break; // QAR
        case 'Kuwait': factor = 0.31; break; // KWD
        case 'Bahrain': factor = 0.38; break; // BHD
        case 'UAE': factor = 3.67; break;  // AED
        case 'Egypt': factor = 47.5; break; // EGP
        case 'Oman': factor = 0.38; break;  // OMR
        default: factor = 1; break; // Default factor (USD)
    }
    // --- END: Conversion Factor Logic ---
    
    // Calculate net balance (using CONVERTED balances)
    const baseDebit = partnerData.baseDebit || 0;
    const baseCredit = partnerData.baseCredit || 0;
    const debitBalance = Math.round(baseDebit * factor); // Apply factor
    const creditBalance = Math.round(baseCredit * factor); // Apply factor
    const netBalance = debitBalance - creditBalance; // Use converted balances
    
    // Get currency from location
    const currencyMap: { [key: string]: string } = {
        'UAE': 'AED',
        'KSA': 'SAR',
        'Qatar': 'QAR',
        'Kuwait': 'KWD',
        'Bahrain': 'BHD',
        'Egypt': 'EGP',
        'Oman': 'OMR',
        'All': 'USD',
    };
    const currency = currencyMap[partnerData.location] || 'USD';
    
    // Generate random trends based on a small percentage of the balance
    const trendPercentage = (Math.random() * 0.2) - 0.1; // Random percentage between -10% and +10%
    const debitTrend = Math.round(debitBalance * trendPercentage); 
    const creditTrend = Math.round(creditBalance * trendPercentage); // Apply the same random percentage for simplicity, or generate another one
    
    // Calculate payout amounts (simplified for demo - using CONVERTED net balance)
    const availableForPayout = Math.round(netBalance * 0.8);
    const payoutPending = Math.round(netBalance * 0.15);
    const payoutOnHold = Math.round(netBalance * 0.05);
    
    // Generate join date (random date in the past 3 years)
    const joinDate = new Date();
    joinDate.setFullYear(joinDate.getFullYear() - Math.floor(Math.random() * 3));
    joinDate.setMonth(Math.floor(Math.random() * 12));
    joinDate.setDate(Math.floor(Math.random() * 28) + 1);
    
    // Create business info (simplified)
    const businessInfo = {
        address: `${partnerData.name} Headquarters\n${partnerData.location}`,
        website: `www.${partnerData.name.toLowerCase().replace(/\s+/g, '')}.com`,
        taxId: `TAX${Math.floor(100000000 + Math.random() * 900000000)}`,
        registration: `REG${Math.floor(100000000 + Math.random() * 900000000)}`,
    };
    
    // Create risk assessment with factors that reflect the risk score
    const riskAssessment = {
        score: riskScore,
        tier: riskScore >= 90 ? 'Platinum Tier' : riskScore >= 80 ? 'Gold Tier' : riskScore >= 70 ? 'Silver Tier' : 'Standard Tier',
        factors: [
            { 
                name: 'Payment History', 
                score: Math.min(100, riskScore + Math.floor(Math.random() * 10)),
                description: riskStatus === 'On Hold' ? 'Payments suspended due to compliance issues' :
                             riskStatus === 'High Risk' ? 'Irregular payment patterns and multiple late payments' :
                             riskStatus === 'Medium Risk' ? 'Occasional delays in payments' :
                             'History of on-time payments and settlements'
            },
            { 
                name: 'Transaction Volume', 
                score: Math.min(100, riskScore + Math.floor(Math.random() * 5)), 
                description: riskStatus === 'On Hold' ? 'Transactions suspended until issues are resolved' :
                             riskStatus === 'High Risk' ? 'Unusual transaction patterns detected' :
                             riskStatus === 'Medium Risk' ? 'Minor inconsistencies in transaction volume' :
                             'Consistent and expected transaction patterns'
            },
            { 
                name: 'Customer Complaints', 
                score: riskStatus === 'On Hold' ? Math.floor(Math.random() * 20) : // Very low score for complaint handling
                       riskStatus === 'High Risk' ? Math.floor(Math.random() * 30) + 10 : // Low score
                       Math.min(100, riskScore - Math.floor(Math.random() * 10)), 
                description: riskStatus === 'On Hold' ? 'Significant number of unresolved customer complaints' :
                             riskStatus === 'High Risk' ? 'High rate of customer complaints requiring attention' :
                             riskStatus === 'Medium Risk' ? 'Moderate level of customer complaints' :
                             'Low rate of customer complaints and issues'
            },
            { 
                name: 'Compliance', 
                score: riskStatus === 'On Hold' ? Math.floor(Math.random() * 15) : // Very low compliance
                       riskStatus === 'High Risk' ? Math.floor(Math.random() * 30) + 20 : // Low compliance
                       Math.min(100, riskScore + Math.floor(Math.random() * 5)),
                description: riskStatus === 'On Hold' ? 'Critical compliance issues requiring immediate attention' :
                             riskStatus === 'High Risk' ? 'Multiple compliance gaps identified' :
                             riskStatus === 'Medium Risk' ? 'Some compliance documentation needs updating' :
                             'Strong adherence to regulatory requirements'
            },
        ],
    };
    
    // Generate sample transactions using the partner's actual products
    const productTypes = partnerData.products ? partnerData.products : ['BNPL', 'Tabby Card', 'Acquiring', 'Capital', 'Postpaid Ads'];
    const transactionTypes = ['VAT', 'Sale', 'Commission', 'Non-revenue corrections', 'Revenue corrections', 'Refund', 'Ads spend', 'VAT on Fees'] as const;
    const statusTypes = ['Completed', 'Pending', 'Failed'] as const;
    const categoryTypes = ['Credit', 'Debit', 'Correction'] as const;
    
    // Generate 50 transactions
    const recentTransactions = Array(50).fill(null).map((_, index) => {
        const type = transactionTypes[Math.floor(Math.random() * transactionTypes.length)];
        // Use actual products from the partner data
        const product = productTypes[Math.floor(Math.random() * productTypes.length)];
        const status = statusTypes[Math.floor(Math.random() * statusTypes.length)];
        const category = categoryTypes[Math.floor(Math.random() * categoryTypes.length)];
        
        // Generate a random amount based on transaction type
        let amount = Math.floor(Math.random() * 800) + 8;
        // Make some amounts larger
        if (Math.random() > 0.7) {
            amount = Math.floor(Math.random() * 600) + 100;
        }
        // Make refunds negative
        if (type === 'Refund' || (Math.random() > 0.7 && category === 'Debit')) {
            amount = -amount;
        }
        
        // Generate a date in the past 10 days
        const date = new Date();
        date.setDate(date.getDate() - Math.floor(Math.random() * 10));
        // Set random hours and minutes
        date.setHours(Math.floor(Math.random() * 24));
        date.setMinutes(Math.floor(Math.random() * 60));
        
        return {
            id: generateUUID(), // Use UUID for ID field
            type,
            product,
            amount,
            date: date.toISOString(),
            status,
            category
        };
    });
    
    // Generate random contact persons
    const contactPersons = [
        {
            id: 'c1',
            name: `${['John', 'Sarah', 'Mohammed', 'Fatima', 'David'][Math.floor(Math.random() * 5)]} ${['Smith', 'Johnson', 'Al Maktoum', 'Al Saud', 'Kumar'][Math.floor(Math.random() * 5)]}`,
            role: 'Finance Director',
            email: `finance@${partnerData.name.toLowerCase().replace(/\s+/g, '')}.com`,
            phone: `+971 50 ${Math.floor(1000000 + Math.random() * 9000000)}`,
            avatarUrl: null
        },
        {
            id: 'c2',
            name: `${['Ahmed', 'Michael', 'Layla', 'Aisha', 'Raj'][Math.floor(Math.random() * 5)]} ${['Ali', 'Williams', 'Khan', 'Chen', 'Gupta'][Math.floor(Math.random() * 5)]}`,
            role: 'Operations Manager',
            email: `operations@${partnerData.name.toLowerCase().replace(/\s+/g, '')}.com`,
            phone: `+971 50 ${Math.floor(1000000 + Math.random() * 9000000)}`,
            avatarUrl: null
        }
    ];
    
    // Use provided risk status directly if available
    const finalRiskStatus = partnerData.riskStatus || (
        riskScore < 50 ? 'High Risk' :
        riskScore < 70 ? 'Medium Risk' : 'Low Risk'
    );
    
    console.log("Partner data with risk status:", partnerData.name, finalRiskStatus, "Score:", riskScore);
    
    return {
        id,
        name: partnerData.name,
        logoUrl: partnerData.logoUrl,
        status: partnerData.status,
        tier: riskAssessment.tier,
        location: partnerData.location, // Keep original location string
        joinedDate: joinDate.toISOString(),
        products: partnerData.products,
        riskScore,
        riskStatus: finalRiskStatus, // Use the determined risk status
        debitBalance, // Use converted value
        debitTrend,
        creditBalance, // Use converted value
        creditTrend,
        netBalance, // Use converted value
        availableForPayout, // Use converted value
        payoutPending, // Use converted value
        payoutOnHold, // Use converted value
        currency, // Use currency code (e.g., AED) for formatting symbol
        businessInfo,
        contactPersons,
        riskAssessment,
        recentTransactions,
    };
};

// --- Helper Functions (similar to TransactionsPage) ---
const formatCurrency = (amount: number, currency: string, hideSymbol = false): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: hideSymbol ? 'decimal' : 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Remove currency symbol if needed, handle potential negative sign placement
  let formatted = formatter.format(Math.abs(amount));
  if (hideSymbol) {
    formatted = formatted.replace(/[^\d.,]/g, '').trim(); // Keep only numbers, comma, dot
  }
   return (amount < 0 ? '-' : '') + formatted;
};

const getInitials = (name: string): string => {
  if (!name) return '??';
  const words = name.trim().split(' ').filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  const firstInitial = words[0][0];
  const lastInitial = words[words.length - 1][0];
  return (firstInitial + lastInitial).toUpperCase();
};

const formatDateTime = (isoString: string): string => {
  try {
    const dateObj = new Date(isoString);
    return dateObj.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    console.error("Error formatting date:", isoString, error);
    return 'Invalid Date';
  }
};

const formatDate = (dateString: string): string => {
    try {
        const dateObj = new Date(dateString);
        return dateObj.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        }); // March 15, 2021
    } catch (error) {
        console.error("Error formatting date:", dateString, error);
        return 'Invalid Date';
    }
};

// --- Reusable Components ---

// Status Badge (similar to TransactionsPage, adapted for PartnerDetails)
const StatusBadge: React.FC<{ status: Transaction['status'] | PartnerDetails['status'] | RiskFactor['score'] }> = ({ status }) => {
  let styles: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'capitalize',
    display: 'inline-block',
  };
  let text = status;

  switch (status) {
    // Partner Status
    case 'Active':
        styles = { ...styles, backgroundColor: '#ecfdf5', color: '#059669' };
        break;
    case 'Inactive':
        styles = { ...styles, backgroundColor: '#fef2f2', color: '#dc2626' };
        break;
    // Transaction Status
    case 'Completed':
      styles = { ...styles, backgroundColor: '#ecfdf5', color: '#059669' };
      break;
    case 'Pending':
       styles = { ...styles, backgroundColor: '#fefce8', color: '#ca8a04' }; 
      break;
    case 'Failed':
      styles = { ...styles, backgroundColor: '#fef2f2', color: '#dc2626' };
      break;
    case 'Refunded':
       styles = { ...styles, backgroundColor: '#eff6ff', color: '#3b82f6' };
       break;
    default:
      styles = { ...styles, backgroundColor: '#f8fafc', color: '#64748b' };
      break;
  }

  return <span style={styles}>{text}</span>;
};

// Tier Badge
const TierBadge: React.FC<{ tier: string }> = ({ tier }) => {
    let styles: React.CSSProperties = {
        padding: '3px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        display: 'inline-block',
    };
    // Basic styling, can be expanded based on actual tier types
    if (tier.toLowerCase().includes('platinum')) {
        styles = { ...styles, backgroundColor: '#f5f3ff', color: '#7c3aed' }; // Purple-ish
    } else {
        styles = { ...styles, backgroundColor: '#f8fafc', color: '#64748b' }; // Default gray
    }
    return <span style={styles}>{tier}</span>;
};

// Product Tag
const ProductTag: React.FC<{ product: string }> = ({ product }) => {
    let bgColor = '#f8fafc';
    let textColor = '#334155';
    
    // Assign different colors based on product type
    switch(product.toLowerCase()) {
        case 'bnpl':
            bgColor = '#f0f9ff';
            textColor = '#0369a1';
            break;
        case 'acquiring':
            bgColor = '#fef3c7';
            textColor = '#92400e';
            break;
        case 'capital':
            bgColor = '#f0fdf4';
            textColor = '#15803d';
            break;
        default:
            bgColor = '#f8fafc';
            textColor = '#334155';
    }
    
    return (
        <span style={{
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            backgroundColor: bgColor,
            color: textColor,
            marginRight: '8px',
            textTransform: 'capitalize',
            display: 'inline-block',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        }}>
            {product}
        </span>
    );
};

// Balance Card Component
interface BalanceCardProps {
    title: string;
    amount: number;
    currency: string;
    description: string;
    trend?: number; // Positive or negative percentage
    positiveIsGood?: boolean;
    details?: React.ReactNode;
}

const BalanceCard: React.FC<BalanceCardProps> = ({ title, amount, currency, description, trend, positiveIsGood = true, details }) => {
    const trendColor = trend !== undefined ? (trend > 0 ? (positiveIsGood ? '#10b981' : '#ef4444') : (positiveIsGood ? '#ef4444' : '#10b981')) : '#6b7280';
    const trendIcon = trend !== undefined ? (trend > 0 ? '▲' : '▼') : '';
    const boldShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'; // Bolder shadow

    return (
        <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: boldShadow, // Updated to bolder shadow
            flex: 1, // Make cards share space
            minWidth: '200px', // Prevent extreme squishing
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 500, color: '#64748b' }}>{title}</h3>
                {trend !== undefined && (
                    <span style={{ fontSize: '13px', fontWeight: 500, color: trendColor, display: 'flex', alignItems: 'center' }}>
                        {trendIcon} {Math.abs(trend)}%
                    </span>
                )}
                {/* Special case for Net Balance Positive tag */}
                {title === 'Net Balance' && amount >= 0 && (
                     <span style={{ fontSize: '12px', fontWeight: 500, color: '#059669', backgroundColor: '#ecfdf5', padding: '2px 8px', borderRadius: '10px' }}>
                         Positive
                     </span>
                 )}
            </div>
            <div style={{ fontSize: '28px', fontWeight: 600, color: '#0f172a', marginBottom: '8px', letterSpacing: '-0.5px' }}>
                {formatCurrency(amount, currency)}
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{description}</p>
            {details && <div style={{ marginTop: '16px', fontSize: '13px', color: '#64748b' }}>{details}</div>}
        </div>
    );
};

// Risk Factor Item
const RiskFactorItem: React.FC<{ factor: RiskFactor }> = ({ factor }) => {
    // Use more gentle colors
    const scoreColor = factor.score >= 90 ? '#34d399' : factor.score >= 70 ? '#fbbf24' : '#f87171';
    const progressBgColor = '#f1f5f9';
    const progressGradient = `linear-gradient(to right, ${scoreColor} ${factor.score}%, ${progressBgColor} ${factor.score}%)`;

    return (
        <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={14} style={{ color: '#34d399', marginRight: '6px' }} />
                    {factor.name}
                </span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#4b5563' }}>{factor.score}</span>
            </div>
            <div style={{ height: '4px', borderRadius: '4px', background: progressGradient, marginBottom: '4px' }}></div>
            <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>{factor.description}</p>
        </div>
    );
};

// Helper component for label-value pairs
const DetailRow: React.FC<{ label: string; value: React.ReactNode; valueAlign?: 'left' | 'right' }> = ({ label, value, valueAlign = 'right' }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9', fontSize: '14px' }}>
        <span style={{ color: '#64748b' }}>{label}</span>
        <span style={{ color: '#111827', fontWeight: 500, textAlign: valueAlign }}>{value}</span>
    </div>
);

// Helper component for card sections
const CardSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.25px' }}>{title}</h3>
        {children}
    </div>
);

// Mapping for country codes (add more as needed)
const countryCodeToName: { [key: string]: string } = {
    'KSA': 'Saudi Arabia',
    'UAE': 'United Arab Emirates',
    // Add other mappings from transformPartnerData
    'Qatar': 'Qatar',
    'Kuwait': 'Kuwait',
    'Bahrain': 'Bahrain',
    'Egypt': 'Egypt',
    'Oman': 'Oman',
};

// --- Upcoming Payment Widget ---
interface UpcomingPaymentWidgetProps {
    currency: string;
    upcomingAmount: number;
    expectedDate: string; // e.g., "May 7"
    daysUntilUpcoming: number; // e.g., 5
    settlementCycle: string; // e.g., "Once a week"
    settlementModel: string; // e.g., "Flexible"
    previousAmount: number;
    previousDate: string; // e.g., "April 30, 2025"
    previousStatus: 'Completed'; // Assuming only completed for previous
}

const UpcomingPaymentWidget: React.FC<UpcomingPaymentWidgetProps> = ({
    currency,
    upcomingAmount,
    expectedDate,
    daysUntilUpcoming,
    settlementCycle,
    settlementModel,
    previousAmount,
    previousDate,
    previousStatus
}) => {
    // TODO: Implement actual date formatting if needed
    const formattedExpectedDate = expectedDate;
    const formattedPreviousDate = previousDate;

    const primaryColor = '#7c3aed'; // Purple
    const secondaryColor = '#a78bfa'; // Lighter Purple
    const lightBgColor = '#f5f3ff'; // Very Light Purple
    const textColor = '#374151';
    const lightTextColor = '#6b7280';
    const greenColor = '#10b981';
    const lightGreenBg = '#ecfdf5';
    const cardBg = '#ffffff';
    const cardBorder = '#e5e7eb';
    const iconBg = '#ede9fe'; // Light purple for icon backgrounds

    // Helper for title style
    const titleStyle: React.CSSProperties = {
        fontSize: '14px',
        fontWeight: 500,
        color: textColor,
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    };

    // Helper for title icon
    const TitleIcon = () => (
        <svg width="4" height="16" viewBox="0 0 4 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="4" height="16" rx="2" fill={primaryColor}/>
        </svg>
    );

    return (
        <div style={{ // Added border style
            border: '1px solid rgb(243, 244, 246)',
            backgroundColor: cardBg,
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            padding: '20px',
            height: '200px', // Fixed height as requested
            display: 'flex',
            flexDirection: 'column',
            flex: '2 0 450px', // Adjusted: grow=2, shrink=0, basis=450px
            minWidth: '450px', // Adjusted minimum width
        }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ backgroundColor: iconBg, padding: '6px', borderRadius: '6px' }}>
                        {/* Calendar SVG */}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 7H4C3.44772 7 3 7.44772 3 8V18C3 19.1046 3.89543 20 5 20H19C20.1046 20 21 19.1046 21 18V8C21 7.44772 20.5523 7 20 7Z" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 3V7M8 3V7M3 12H21" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M7 16H7.01" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 16H12.01" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: textColor }}>Payouts</h3>
                </div>
                {/* REMOVED Upcoming Days Badge */}
            </div>

            {/* Body Content */}
            <div style={{ display: 'flex', flex: 1, gap: '16px' }}>
                {/* Payment Amount Card -> Upcoming Payout Card */}
                <div style={{
                    flex: 1,
                    backgroundColor: cardBg,
                    borderRadius: '10px',
                    padding: '16px',
                    border: `1px solid ${cardBorder}`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start', // Align content to top
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                }}>
                    {/* Updated Title Style & Text & Margin */}
                    <div style={{ ...titleStyle, marginBottom: '20px' /* Increased space */ }}>
                        <TitleIcon />
                        Upcoming Payout {/* Renamed */}
                    </div>
                    {/* Updated Amount Formatting */}
                    <div style={{ fontSize: '26px', fontWeight: 700, color: primaryColor, lineHeight: 1.1, marginBottom: '10px' }}>
                        {formatCurrency(upcomingAmount, currency)}
                    </div>
                    {/* ADDED: marginTop: 'auto' to push to bottom */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: greenColor, marginTop: 'auto' }}>
                        <CalendarIcon size={14} color={greenColor} />
                        Expected on {formattedExpectedDate}
                    </div>
                </div>

                {/* Previous Payout Card (Swapped) */}
                <div style={{
                    flex: 1,
                    backgroundColor: cardBg,
                    borderRadius: '10px',
                    padding: '16px',
                    border: `1px solid ${cardBorder}`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start', // Align content to top
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                }}>
                     {/* Title using helper & Margin */}
                     <div style={{ ...titleStyle, marginBottom: '20px' /* Increased space */ }}>
                         <TitleIcon />
                         Previous Payout
                     </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                         <div style={{ backgroundColor: lightGreenBg, width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CheckCircleIcon size={16} color={greenColor} />
                        </div>
                        <div>
                            {/* Updated Amount Formatting */}
                            <div style={{ fontSize: '16px', fontWeight: 600, color: textColor }}>{formatCurrency(previousAmount, currency)}</div>
                            <div style={{ fontSize: '12px', color: lightTextColor }}>{formattedPreviousDate}</div>
                        </div>
                    </div>
                     <span style={{
                        alignSelf: 'flex-start', // Align badge to the left
                        fontSize: '12px',
                        fontWeight: 500,
                        color: greenColor,
                        backgroundColor: lightGreenBg,
                        padding: '3px 8px',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: 'auto' // Pushes to bottom
                    }}>
                        <span style={{ width: '6px', height: '6px', backgroundColor: greenColor, borderRadius: '50%' }}></span>
                        {previousStatus}
                    </span>
                </div>

                {/* Settlement Details Card (Swapped & Modified) */}
                <div style={{
                    flex: 1.2, // Slightly wider
                    backgroundColor: cardBg,
                    borderRadius: '10px',
                    padding: '16px',
                    border: `1px solid ${cardBorder}`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start', // Align content to top
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                }}>
                    {/* Updated Title Style & Margin */}
                     <div style={{ ...titleStyle, marginBottom: '20px' /* Increased space */ }}>
                         <TitleIcon />
                         Settlement Details
                     </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                             <div style={{ backgroundColor: iconBg, width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Clock size={16} color={primaryColor} />
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: lightTextColor }}>Settlement Cycle</div>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: textColor }}>{settlementCycle}</div>
                            </div>
                        </div>
                        {/* REMOVED Learn More Link */}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ backgroundColor: iconBg, width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <BarChart2 size={16} color={primaryColor} />
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: lightTextColor }}>Settlement Model</div>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: textColor }}>{settlementModel}</div>
                            </div>
                        </div>
                        {/* REMOVED Change Button */}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main Page Component ---
const PartnerDetailsPage: React.FC = () => {
    const { partnerId } = useParams<{ partnerId: string }>();
    const [partner, setPartner] = React.useState<PartnerDetails | null>(null);
    const [activeTab, setActiveTab] = React.useState<'Overview' | 'Transactions' | 'Risk report' | 'Company Details'>('Overview'); // Renamed 'Contact Info'
    const [searchTerm, setSearchTerm] = React.useState(''); // For transactions search
    // Add state for ID copy behavior
    const [hoveredIdIcon, setHoveredIdIcon] = React.useState<string | null>(null);
    const [copiedId, setCopiedId] = React.useState<string | null>(null);

    // TODO: Add state for filters if needed
    const [transactionTypeFilter, setTransactionTypeFilter] = React.useState('All Types');
    const [transactionStatusFilter, setTransactionStatusFilter] = React.useState('All Statuses');

    // --- Pagination State for Transactions Tab ---
    const [currentPage, setCurrentPage] = React.useState(1);
    const itemsPerPage = 20; // Number of transactions per page

    // Generate mock graph data for balance cards
    const generateMockGraphData = (baseValue: number, points: number = 20, uptrend: boolean = true): GraphDataPoint[] => {
        const trend = uptrend ? 1 : -1;
        const volatility = baseValue * 0.08;
        const trendFactor = baseValue * 0.01;
        
        return Array.from({ length: points }, (_, i): GraphDataPoint => {
            const trendValue = trendFactor * i * trend;
            const noise = (Math.random() - 0.5) * volatility;
            return {
                name: `Point ${i + 1}`,
                value: Math.max(baseValue * 0.1, Math.round(baseValue + trendValue + noise))
            };
        });
    };

    // Generate mock graph data based on partner balances
    const debitGraphData = React.useMemo(() => {
        return partner ? generateMockGraphData(partner.debitBalance, 20, true) : [];
    }, [partner]);

    const creditGraphData = React.useMemo(() => {
        return partner ? generateMockGraphData(partner.creditBalance, 20, false) : [];
    }, [partner]);

    // Effect to "fetch" partner data based on URL params
    React.useEffect(() => {
        // Find the partner data by converting partnerId back to a potential name
        const partnerName = partnerId?.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        
        // First, check if the partner is in the risk lists (onHold or highRisk)
        const onHoldPartner = mockOnHoldPartners.find(p => 
            p.name.toLowerCase() === partnerName?.toLowerCase()
        );
        
        const highRiskPartner = mockHighRiskPartners.find(p => 
            p.name.toLowerCase() === partnerName?.toLowerCase()
        );
        
        let riskStatusPartner = onHoldPartner || highRiskPartner;
        
        // Find matching partner in partnersData
        const foundPartner = partnersData.find(p => 
            p.name.toLowerCase() === partnerName?.toLowerCase()
        );
        
        if (foundPartner) {
            // If the partner exists in risk lists, apply its risk status
            if (riskStatusPartner) {
                // Use type assertion to avoid TypeScript errors
                (foundPartner as any).riskStatus = riskStatusPartner.riskStatus;
                // If risk status not in partner data, add it
                if (!foundPartner.risk) {
                    foundPartner.risk = riskStatusPartner.risk;
                }
            }
            
            // Transform the partner data to the format needed for this page
            const transformedPartner = transformPartnerData(foundPartner);
            setPartner(transformedPartner);
        } else if (riskStatusPartner) {
            // If partner is only in risk lists but not in partnersData, use it directly
            const transformedPartner = transformPartnerData(riskStatusPartner);
            setPartner(transformedPartner);
        } else {
            // Fallback to mock data if partner not found
            console.warn(`Partner not found for ID: ${partnerId}, using mock data`);
            // Ensure mock data also has enough transactions if fallback is used
            const mockPartnerWithMoreTx = {
                ...mockPartner,
                recentTransactions: transformPartnerData(mockPartner).recentTransactions // Generate more for mock too
            };
            setPartner(mockPartnerWithMoreTx);
        }
    }, [partnerId]);

    // --- Filtering for Overview Tab (remains the same) ---
    const filteredRecentTransactions = partner?.recentTransactions.filter(tx => {
         const searchTermLower = searchTerm.toLowerCase(); // Using same search term for simplicity, can be separated
         const matchesSearch = searchTerm === '' ||
                               tx.id.toLowerCase().includes(searchTermLower) ||
                               tx.type.toLowerCase().includes(searchTermLower) ||
                               tx.product.toLowerCase().includes(searchTermLower) ||
                               tx.status.toLowerCase().includes(searchTermLower) ||
                               tx.amount.toString().includes(searchTermLower);

         const matchesType = transactionTypeFilter === 'All Types' || tx.type === transactionTypeFilter; // Using same filters for simplicity
         const matchesStatus = transactionStatusFilter === 'All Statuses' || tx.status === transactionStatusFilter;

         return matchesSearch && matchesType && matchesStatus;
    }).slice(0, 9) || []; // Show up to 9 transactions

    // --- Filtering and Pagination for Transactions Tab ---
    const allTransactions = partner?.recentTransactions || [];
    const filteredAllTransactions = allTransactions.filter(tx => {
         const searchTermLower = searchTerm.toLowerCase();
         const matchesSearch = searchTerm === '' ||
                               tx.id.toLowerCase().includes(searchTermLower) ||
                               tx.type.toLowerCase().includes(searchTermLower) ||
                               tx.product.toLowerCase().includes(searchTermLower) ||
                               tx.status.toLowerCase().includes(searchTermLower) ||
                               tx.amount.toString().includes(searchTermLower);

         const matchesType = transactionTypeFilter === 'All Types' || tx.type === transactionTypeFilter;
         const matchesStatus = transactionStatusFilter === 'All Statuses' || tx.status === transactionStatusFilter;

         return matchesSearch && matchesType && matchesStatus;
    });

    const totalPages = Math.ceil(filteredAllTransactions.length / itemsPerPage);
    const paginatedTransactions = filteredAllTransactions.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // --- Pagination Handlers ---
    const handleNextPage = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    const handlePreviousPage = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const handlePageClick = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Reset page to 1 if filters change and current page becomes invalid
    React.useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        } else if (currentPage < 1 && totalPages > 0) {
             setCurrentPage(1);
        } else if (totalPages === 0 && currentPage !== 1) { // Reset to 1 if no results
             setCurrentPage(1); 
        }
    }, [currentPage, totalPages]);

    // Basic filtering for demo
    const filteredTransactions = partner?.recentTransactions.filter(tx => {
         const searchTermLower = searchTerm.toLowerCase();
         const matchesSearch = searchTerm === '' ||
                               tx.id.toLowerCase().includes(searchTermLower) ||
                               tx.type.toLowerCase().includes(searchTermLower) ||
                               tx.product.toLowerCase().includes(searchTermLower) ||
                               tx.status.toLowerCase().includes(searchTermLower) ||
                               tx.amount.toString().includes(searchTermLower);

         const matchesType = transactionTypeFilter === 'All Types' || tx.type === transactionTypeFilter;
         const matchesStatus = transactionStatusFilter === 'All Statuses' || tx.status === transactionStatusFilter;

         return matchesSearch && matchesType && matchesStatus;
    }).slice(0, 9) || []; // Show up to 9 transactions

    // TODO: Replace with actual filter options derived from data
    const transactionTypeOptions = ['All Types', 'Purchase', 'Refund', 'Payout'];
    const transactionStatusOptions = ['All Statuses', 'Completed', 'Pending', 'Failed', 'Refunded'];

    // Add handler for copying ID
    const handleCopyID = async (id: string) => {
        try {
            await navigator.clipboard.writeText(id);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 1500); // Reset icon after 1.5 seconds
        } catch (err) {
            console.error('Failed to copy ID: ', err);
        }
    };

    if (!partner) {
        return (
            <MainLayout>
                <div style={{ padding: '24px', textAlign: 'center' }}>
                    <h2>Loading partner details...</h2>
                    {/* You could add a loading spinner here */}
                </div>
            </MainLayout>
        );
    }

    // Mock Data for Upcoming Payment Widget
    const upcomingPaymentMockData = {
        currency: partner.currency,
        upcomingAmount: 135789, // SAR 1305.0K // Updated to a random value in range
        expectedDate: "May 7",
        daysUntilUpcoming: 5,
        settlementCycle: "Once a week",
        settlementModel: "Fixed",
        previousAmount: 976200, // SAR 976.2K
        previousDate: "April 30, 2025",
        previousStatus: "Completed" as const,
    };

    // Partner Risk Status Badges (copied from RiskManagementPage)
    const RiskStatusBadge: React.FC<{ status: 'On Hold' | 'High Risk' | 'Medium Risk' | 'Low Risk' | null }> = ({ status }) => {
      if (!status) return null;
      
      let backgroundColor = '';
      let color = '';
      switch (status) {
        case 'On Hold': backgroundColor = '#fee2e2'; color = '#dc2626'; break; // Red
        case 'High Risk': backgroundColor = '#fef9c3'; color = '#ca8a04'; break; // Yellow/Amber
        case 'Medium Risk': backgroundColor = '#f3f4f6'; color = '#6b7280'; break; // Gray
        case 'Low Risk': backgroundColor = '#d1fae5'; color = '#059669'; break; // Green
      }
      return <span style={{ backgroundColor, color, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>{status}</span>;
    };

    // Inside the PartnerDetailsPage component, immediately after the outer div
    return (
        <MainLayout>
            <div style={{ padding: '28px', backgroundColor: '#ffffff', minHeight: '100vh' }}>
                {/* Risk Status Banner - Improved Visibility */}
                {partner.riskStatus && (partner.riskStatus === 'On Hold' || partner.riskStatus === 'High Risk') && (
                    <div 
                        style={{ 
                            backgroundColor: partner.riskStatus === 'On Hold' ? '#fee2e2' : '#fef9c3',
                            color: partner.riskStatus === 'On Hold' ? '#dc2626' : '#ca8a04',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            fontSize: '14px',
                            fontWeight: '500',
                            border: `1px solid ${partner.riskStatus === 'On Hold' ? '#fca5a5' : '#fde68a'}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                            <span>
                                {partner.riskStatus === 'On Hold' 
                                    ? 'This partner is currently on hold. All transactions are suspended.' 
                                    : 'This partner is flagged as high risk. Monitor transactions carefully.'}
                            </span>
                        </div>
                        <div style={{ 
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            padding: '4px 12px',
                            fontSize: '12px',
                            fontWeight: '600'
                        }}>
                            {partner.riskStatus}
                        </div>
                    </div>
                )}
                {/* --- Page Header --- */}
                <div style={{ marginBottom: '28px' }}>
                    {/* Back Link */}
                    <button 
                        onClick={() => window.history.back()}
                        style={{ 
                            background: 'none', 
                            border: 'none', 
                            padding: 0, 
                            marginBottom: '18px', 
                            cursor: 'pointer', 
                            color: '#64748b', 
                            fontSize: '14px', 
                            display: 'flex', 
                            alignItems: 'center',
                            transition: 'color 0.2s ease'
                        }}
                    >
                        <ArrowLeft size={16} style={{ marginRight: '6px' }} />
                        Back to Partners
                    </button>

                    {/* Partner Info Header Card */}
                    <div style={{ 
                        border: '1px solid rgb(243, 244, 246)', // Added border
                        backgroundColor: 'white', 
                        borderRadius: '12px', 
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        padding: '24px',
                        marginBottom: '16px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap' }}>
                            {/* Left Side: Logo, Name, Details */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                {/* Logo */}
                                <div style={{ 
                                    width: '64px', 
                                    height: '64px', 
                                    borderRadius: '12px', 
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    overflow: 'hidden', 
                                    backgroundColor: 'white' 
                                }}>
                                    {partner.logoUrl ? (
                                        <img 
                                            src={partner.logoUrl} 
                                            alt={`${partner.name} logo`} 
                                            style={{ maxWidth: '75%', maxHeight: '75%', objectFit: 'contain' }} 
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                const fallback = e.currentTarget.parentElement?.querySelector('.fallback-initials') as HTMLElement;
                                                if (fallback) {
                                                    fallback.style.display = 'block';
                                                }
                                            }}
                                        />
                                    ) : (
                                        <span style={{ fontSize: '24px', fontWeight: 600, color: '#64748b' }}>{getInitials(partner.name)}</span>
                                    )}
                                    <span 
                                        className="fallback-initials" 
                                        style={{ 
                                            display: partner.logoUrl ? 'none' : 'block',
                                            fontSize: '24px', 
                                            fontWeight: 600, 
                                            color: '#64748b' 
                                        }}
                                    >
                                        {getInitials(partner.name)}
                                    </span>
                                </div>
                                {/* Name & Details */}
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                                        <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.5px' }}>{partner.name}</h1>
                                        <StatusBadge status={partner.status} />
                                        <TierBadge tier={partner.tier} />
                                        {partner.riskStatus && <RiskStatusBadge status={partner.riskStatus} />}
                                    </div>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        color: '#64748b', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '16px', 
                                        flexWrap: 'wrap', 
                                        marginBottom: '10px',
                                        borderLeft: '3px solid #f1f5f9',
                                        paddingLeft: '10px'
                                    }}>
                                        <span>ID: {partner.id}</span>
                                        <span style={{ display: 'flex', alignItems: 'center' }}><MapPin size={14} style={{ marginRight: '4px' }} /> {partner.location}</span>
                                        <span>Joined {formatDate(partner.joinedDate)}</span>
                                    </div>
                                    {/* Product Tags */}
                                    <div>
                                        {partner.products.map(p => <ProductTag key={p} product={p} />)}
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Risk Score, Actions */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                <div style={{ 
                                    textAlign: 'right', 
                                    padding: '8px 14px', 
                                    borderRadius: '10px', 
                                    backgroundColor: partner.riskScore >= 80 ? '#ecfdf5' : partner.riskScore >= 60 ? '#fffbeb' : '#fef2f2'
                                }}>
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>Risk Score: </span>
                                    <span style={{ 
                                        fontSize: '18px', 
                                        fontWeight: 600, 
                                        color: partner.riskScore >= 80 ? '#059669' : partner.riskScore >= 60 ? '#d97706' : '#dc2626' 
                                    }}>
                                        {partner.riskScore}%
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {/* Action Buttons */}
                                    <button style={{ 
                                        padding: '9px 14px', 
                                        borderRadius: '8px', 
                                        border: 'none', 
                                        backgroundColor: '#f8fafc', 
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)', 
                                        fontSize: '14px', 
                                        fontWeight: 500, 
                                        color: '#334155', 
                                        cursor: 'pointer', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '6px',
                                        transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                                    }}>
                                        <Edit size={16} /> Edit
                                    </button>
                                    <button style={{ 
                                        padding: '9px 14px', 
                                        borderRadius: '8px', 
                                        border: 'none', 
                                        backgroundColor: '#f8fafc', 
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)', 
                                        fontSize: '14px', 
                                        fontWeight: 500, 
                                        color: '#334155', 
                                        cursor: 'pointer', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '6px',
                                        transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                                    }}>
                                        <Download size={16} /> Export
                                    </button>
                                    <button style={{ 
                                        padding: '9px', 
                                        borderRadius: '8px', 
                                        border: 'none', 
                                        backgroundColor: '#f8fafc', 
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)', 
                                        fontSize: '14px', 
                                        fontWeight: 500, 
                                        color: '#334155', 
                                        cursor: 'pointer',
                                        transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                                    }}>
                                        <MoreHorizontal size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Date Filter - Moved outside the partner info card */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        marginBottom: '16px'
                    }}>
                        <div style={{ position: 'relative' }}>
                            <button style={{ 
                                padding: '9px 14px', 
                                borderRadius: '8px', 
                                border: 'none', 
                                backgroundColor: 'white', 
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', 
                                fontSize: '14px', 
                                fontWeight: 500, 
                                color: '#334155', 
                                cursor: 'pointer', 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '6px',
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                            }}>
                                <Calendar size={16} />
                                This Month
                                <ChevronDown size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* --- Balance Cards & Upcoming Payment --- */}
                {/* UPDATED: Wrapper div with flex display */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '28px', flexWrap: 'wrap', alignItems: 'stretch' }}>
                    <TotalBalanceCard
                        title="Debit Balance"
                        amount={partner.debitBalance}
                        currency={partner.currency}
                        trend={partner.debitTrend}
                        graphData={debitGraphData}
                    />
                    <TotalBalanceCard
                        title="Credit Balance"
                        amount={partner.creditBalance}
                        currency={partner.currency}
                        trend={partner.creditTrend}
                        graphData={creditGraphData}
                    />
                    {/* NEW: Upcoming Payment Widget */}
                    <UpcomingPaymentWidget {...upcomingPaymentMockData} />
                </div>

                {/* --- Tabs --- */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', /* boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', */ overflow: 'hidden', marginBottom: '24px' }}>
                    <div style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <nav style={{ display: 'flex', gap: '8px', padding: '0 16px' }}>
                            {(['Overview', 'Transactions', 'Risk report', 'Company Details'] as const).map(tab => ( // Renamed 'Contact Info'
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    style={{
                                        padding: '16px 12px',
                                        margin: '0',
                                        border: 'none',
                                        background: 'none',
                                        fontSize: '14px',
                                        fontWeight: activeTab === tab ? 600 : 500,
                                        color: activeTab === tab ? '#0f172a' : '#64748b',
                                        cursor: 'pointer',
                                        borderBottom: activeTab === tab ? '2px solid #4f46e5' : '2px solid transparent',
                                        marginBottom: '-1px',
                                        transition: 'color 0.2s ease',
                                    }}
                                >
                                    {tab}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* --- Tab Content --- */}
                    <div style={{ display: activeTab === 'Overview' ? 'block' : 'none' }}>
                        {/* Overview Content */}
                        {/* UPDATED: Swapped columns and adjusted gridTemplateColumns */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '28px', padding: '24px' }}>
                            {/* Right Column: Risk (Now Left) */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                                {/* Risk Assessment Card */}
                                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', border: '1px solid #f1f5f9', height: 'calc(100% - 28px)' }}>
                                    <div style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937', letterSpacing: '-0.25px' }}>Risk Assessment</h3>
                                        <span style={{ 
                                            fontSize: '12px', 
                                            fontWeight: '500', 
                                            backgroundColor: partner.riskStatus === 'Low Risk' ? '#f0fdf4' : 
                                                            partner.riskStatus === 'Medium Risk' ? '#fef3c7' : 
                                                            partner.riskStatus === 'High Risk' ? '#fef9c3' : 
                                                            partner.riskStatus === 'On Hold' ? '#fee2e2' : '#f0fdf4', 
                                            color: partner.riskStatus === 'Low Risk' ? '#15803d' : 
                                                    partner.riskStatus === 'Medium Risk' ? '#b45309' : 
                                                    partner.riskStatus === 'High Risk' ? '#ca8a04' : 
                                                    partner.riskStatus === 'On Hold' ? '#dc2626' : '#15803d', 
                                            padding: '4px 8px', 
                                            borderRadius: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span style={{ 
                                                width: '6px', 
                                                height: '6px', 
                                                backgroundColor: partner.riskStatus === 'Low Risk' ? '#22c55e' : 
                                                               partner.riskStatus === 'Medium Risk' ? '#f59e0b' : 
                                                               partner.riskStatus === 'High Risk' ? '#ca8a04' : 
                                                               partner.riskStatus === 'On Hold' ? '#dc2626' : '#22c55e', 
                                                borderRadius: '50%' 
                                            }}></span>
                                            {partner.riskStatus}
                                        </span>
                                    </div>
                                    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', height: 'calc(100% - 53px)' }}>
                                        <div style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '16px',
                                            marginBottom: '16px', 
                                            backgroundColor: '#fafafa', 
                                            padding: '16px', 
                                            borderRadius: '8px'
                                        }}>
                                            <div style={{
                                                width: '70px',
                                                height: '70px',
                                                borderRadius: '50%',
                                                background: `conic-gradient(${
                                                    partner.riskStatus === 'Low Risk' ? '#34d399' : 
                                                    partner.riskStatus === 'Medium Risk' ? '#fbbf24' : 
                                                    partner.riskStatus === 'High Risk' ? '#f87171' : 
                                                    partner.riskStatus === 'On Hold' ? '#ef4444' : '#34d399'
                                                } ${partner.riskAssessment.score}%, #f1f5f9 0)`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                position: 'relative'
                                            }}>
                                                <div style={{
                                                    width: '54px',
                                                    height: '54px',
                                                    borderRadius: '50%',
                                                    backgroundColor: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <span style={{ 
                                                        fontSize: '18px', 
                                                        fontWeight: 600, 
                                                        color: '#1f2937'
                                                    }}>
                                                        {partner.riskAssessment.score}%
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <div style={{ 
                                                    fontSize: '13px', 
                                                    color: '#6b7280',
                                                    marginBottom: '4px'
                                                }}>
                                                    Risk Score
                                                </div>
                                                <div style={{ 
                                                    fontSize: '15px', 
                                                    fontWeight: 600, 
                                                    color: '#1f2937'
                                                }}>
                                                    {partner.riskStatus === 'Low Risk' ? 'Excellent' : 
                                                    partner.riskStatus === 'Medium Risk' ? 'Good' : 
                                                    partner.riskStatus === 'High Risk' ? 'At Risk' : 
                                                    partner.riskStatus === 'On Hold' ? 'On Hold' : 
                                                    partner.riskAssessment.score >= 80 ? 'Excellent' : 
                                                    partner.riskAssessment.score >= 60 ? 'Good' : 'Needs Attention'}
                                                </div>
                                                <div style={{ 
                                                    fontSize: '12px',
                                                    color: '#6b7280',
                                                    marginTop: '4px'
                                                }}>
                                                    Last updated: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </div>
                                            </div>
                                        </div>
                                        <h4 style={{ 
                                            fontSize: '14px', 
                                            fontWeight: 600, 
                                            color: '#1f2937', 
                                            margin: '0 0 12px 0'
                                        }}>Risk Factors</h4>
                                        <div style={{ flex: 1, overflowY: 'auto' }}>
                                            {partner.riskAssessment.factors.map(factor => (
                                                <RiskFactorItem key={factor.name} factor={factor} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Left Column: Transactions & Business Info (Now Right) */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                                {/* Recent Transactions Card */}
                                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', overflow: 'hidden', height: 'calc(100% - 28px)' }}>
                                    <div style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.25px' }}>Recent Transactions</h3>
                                    </div>
                                    {/* Transaction Table */}
                                    {filteredTransactions.length > 0 ? (
                                        <div style={{ height: 'calc(100% - 53px)', overflowY: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                                <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8fafc' }}>
                                                    <tr style={{ borderBottom: '1px solid #f1f5f9', textAlign: 'left', color: '#64748b' }}>
                                                        <th style={{ padding: '12px 18px', fontWeight: 500, textAlign: 'center' }}>ID</th>
                                                        <th style={{ padding: '12px 18px', fontWeight: 500 }}>Created at</th>
                                                        <th style={{ padding: '12px 18px', fontWeight: 500 }}>Product</th>
                                                        <th style={{ padding: '12px 18px', fontWeight: 500 }}>Category</th>
                                                        <th style={{ padding: '12px 18px', fontWeight: 500 }}>Type</th>
                                                        <th style={{ padding: '12px 18px', fontWeight: 500, textAlign: 'right' }}>Amount</th>
                                                        <th style={{ padding: '12px 18px', fontWeight: 500, textAlign: 'center' }}>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredTransactions.map((tx, index) => {
                                                        const formattedDate = formatDateTime(tx.date);
                                                        const amountColor = tx.amount >= 0 ? '#0f172a' : '#ef4444';

                                                        // Determine the currency code based on a pattern
                                                        // REMOVE THIS BLOCK START
                                                        // let currencyCode = partner.currency;
                                                        // if (index % 5 === 1) currencyCode = 'SAR';
                                                        // if (index % 7 === 0) currencyCode = 'BHD';
                                                        // if (index % 11 === 0) currencyCode = 'KWD';
                                                        // if (index % 13 === 0) currencyCode = 'QAR';
                                                        // REMOVE THIS BLOCK END

                                                        // Use partner.currency directly
                                                        const currencyCode = partner.currency;

                                                        return (
                                                            <tr key={tx.id} style={{
                                                                borderBottom: '1px solid #f1f5f9',
                                                                backgroundColor: 'transparent',
                                                                transition: 'background-color 0.1s ease-in-out',
                                                                height: '35px',
                                                                cursor: 'pointer'
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                                <td style={{ padding: '7px 18px', textAlign: 'center', color: '#6b7280' }}>
                                                                    #
                                                                </td>
                                                                <td style={{ padding: '7px 18px', color: '#334155', whiteSpace: 'nowrap' }}>
                                                                    {formattedDate}
                                                                </td>
                                                                <td style={{ padding: '7px 18px' }}>
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '4px',
                                                                        backgroundColor: '#f1f5f9',
                                                                        color: '#334155',
                                                                        fontSize: '12px',
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {tx.product}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '7px 18px' }}>
                                                                    <span style={{
                                                                        color: tx.category === 'Credit' ? '#047857' :
                                                                              tx.category === 'Debit' ? '#dc2626' : '#6366f1',
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {tx.category || 'Credit'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '7px 18px', color: '#334155' }}>
                                                                    {tx.type}
                                                                </td>
                                                                <td style={{ padding: '7px 18px', fontWeight: 500, textAlign: 'right', color: amountColor }}>
                                                                    {tx.amount < 0 ? '-' : ''}{currencyCode} {formatCurrency(Math.abs(tx.amount), currencyCode, true)}
                                                                </td>
                                                                <td style={{ padding: '7px 18px', textAlign: 'center' }}>
                                                                    <StatusBadge status={tx.status} />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>No recent transactions found.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: activeTab === 'Transactions' ? 'block' : 'none' }}>
                        <div style={{ padding: '24px' }}>
                            {/* Filters (Optional - Reuse/adapt from other pages if needed) */}
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#0f172a' }}>All Transactions</h3>
                                {/* Add Search/Filter inputs here if desired */}
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    {/* Search Input Example */}
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            placeholder="Search ID, type, product..."
                                            value={searchTerm}
                                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} // Reset page on search
                                            style={{
                                                padding: '8px 12px 8px 34px',
                                                borderRadius: '6px',
                                                border: '1px solid #e5e7eb',
                                                fontSize: '13px',
                                                minWidth: '250px'
                                            }}
                                        />
                                        <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                                    </div>
                                     {/* Add Filter dropdowns here if needed */}
                                </div>
                            </div>

                             {/* Transaction Table */}
                            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', overflow: 'hidden' }}>
                                {paginatedTransactions.length > 0 ? (
                                    <div style={{ overflowX: 'auto' }}> {/* Allow horizontal scroll if needed */}
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '800px' }}>
                                            {/* Re-use the same thead structure */}
                                            <thead style={{ backgroundColor: '#f8fafc' }}>
                                                <tr style={{ borderBottom: '1px solid #f1f5f9', textAlign: 'left', color: '#64748b' }}>
                                                    <th style={{ padding: '12px 18px', fontWeight: 500, textAlign: 'center' }}>ID</th>
                                                    <th style={{ padding: '12px 18px', fontWeight: 500 }}>Created at</th>
                                                    <th style={{ padding: '12px 18px', fontWeight: 500 }}>Product</th>
                                                    <th style={{ padding: '12px 18px', fontWeight: 500 }}>Category</th>
                                                    <th style={{ padding: '12px 18px', fontWeight: 500 }}>Type</th>
                                                    <th style={{ padding: '12px 18px', fontWeight: 500, textAlign: 'right' }}>Amount</th>
                                                    <th style={{ padding: '12px 18px', fontWeight: 500, textAlign: 'center' }}>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedTransactions.map((tx) => { // Use paginatedTransactions here
                                                    const formattedDate = formatDateTime(tx.date);
                                                    const amountColor = tx.amount >= 0 ? '#0f172a' : '#ef4444';
                                                    const currencyCode = partner.currency;
                                                    return (
                                                        <tr key={tx.id} style={{
                                                            borderBottom: '1px solid #f1f5f9',
                                                            backgroundColor: 'transparent',
                                                            transition: 'background-color 0.1s ease-in-out',
                                                            height: '35px', // Adjust height if needed
                                                            cursor: 'pointer' // Keep row clickable
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                        >
                                                            {/* Use full ID */}
                                                            <td style={{ padding: '7px 18px', textAlign: 'center', color: '#6b7280', fontFamily: 'monospace', fontSize: '12px' }}>
                                                                {tx.id}
                                                             </td>
                                                             <td style={{ padding: '7px 18px', color: '#334155', whiteSpace: 'nowrap' }}>
                                                                 {formattedDate}
                                                             </td>
                                                            <td style={{ padding: '7px 18px' }}>
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '4px',
                                                                        backgroundColor: '#f1f5f9',
                                                                        color: '#334155',
                                                                        fontSize: '12px',
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {tx.product}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '7px 18px' }}>
                                                                    <span style={{
                                                                        color: tx.category === 'Credit' ? '#047857' :
                                                                              tx.category === 'Debit' ? '#dc2626' : '#6366f1',
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {tx.category || 'Credit'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '7px 18px', color: '#334155' }}>
                                                                    {tx.type}
                                                                </td>
                                                                <td style={{ padding: '7px 18px', fontWeight: 500, textAlign: 'right', color: amountColor, whiteSpace: 'nowrap' }}>
                                                                    {tx.amount < 0 ? '-' : ''}{currencyCode} {formatCurrency(Math.abs(tx.amount), currencyCode, true)}
                                                                </td>
                                                                <td style={{ padding: '7px 18px', textAlign: 'center' }}>
                                                                    <StatusBadge status={tx.status} />
                                                                </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div style={{ padding: '40px 24px', textAlign: 'center', color: '#64748b' }}>No transactions found matching your criteria.</div>
                                )}

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '1px solid #f1f5f9', backgroundColor: '#f9fafb' }}>
                                        <button
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                            style={{
                                                padding: '6px 10px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                                                color: currentPage === 1 ? '#9ca3af' : '#374151',
                                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: '13px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            <PaginationChevronLeft size={16} />
                                            Previous
                                        </button>
                                        <span style={{ fontSize: '13px', color: '#6b7280' }}>
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={handleNextPage}
                                            disabled={currentPage === totalPages}
                                             style={{
                                                padding: '6px 10px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                backgroundColor: currentPage === totalPages ? '#f3f4f6' : 'white',
                                                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: '13px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            Next
                                            <PaginationChevronRight size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: activeTab === 'Risk report' ? 'block' : 'none' }}>
                        {/* Risk Report Content */}
                        <div style={{padding: '24px'}}>
                            {/* REMOVED Risk Intelligence Header Div */}

                            {/* Risk Intelligence & Predictions - Moved h3 inside */}
                            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', marginBottom: '24px', padding: '24px' }}>
                                {/* MOVED and styled h3 */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.25px' }}>Risk Intelligence</h3>
                                    {/* Filter/Score button moved here */}
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <div style={{ 
                                            backgroundColor: '#f8fafc',
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '13px',
                                            fontWeight: 500,
                                            color: '#334155'
                                        }}>
                                            <span style={{ 
                                                display: 'inline-block', 
                                                width: '8px', 
                                                height: '8px', 
                                                borderRadius: '50%',
                                                backgroundColor: '#059669'
                                            }}></span>
                                            Risk Score: {partner.riskAssessment.score}%
                                        </div>
                                        <button style={{
                                            padding: '8px 14px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            backgroundColor: '#f8fafc',
                                            fontSize: '13px',
                                            fontWeight: 500,
                                            color: '#334155',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}>
                                            <Filter size={14} /> Filter Insights
                                        </button>
                                    </div>
                                </div>
                                
                                {/* AI Risk Intelligence Alert - Now Integrated */}
                                <div style={{ 
                                    backgroundColor: '#f0f9ff', 
                                    borderRadius: '8px', 
                                    padding: '16px',
                                    marginBottom: '24px',
                                    borderLeft: '4px solid #0ea5e9',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '16px'
                                }}>
                                    <div style={{ 
                                        width: '40px', 
                                        height: '40px', 
                                        borderRadius: '50%', 
                                        backgroundColor: '#e0f2fe',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        marginTop: '2px'
                                    }}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#0284c7', marginBottom: '4px' }}>AI Risk Intelligence</div>
                                        <div style={{ fontSize: '13px', color: '#334155', lineHeight: '1.5' }}>
                                            Based on analysis of the partner's Q2 2024 data, we anticipate a <span style={{ fontWeight: '500' }}>25% transaction volume increase</span> in June-July due to seasonal patterns. We recommend temporarily increasing authorization limits by 15% during this period.
                                            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '5px', color: '#0284c7', fontWeight: '500', fontSize: '12px', cursor: 'pointer' }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15 5L20 10M20 10L15 15M20 10H10M17 19H5C3.89543 19 3 18.1046 3 17V7C3 5.89543 3.89543 5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                View detailed AI analysis
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ position: 'absolute', top: '12px', right: '12px', backgroundColor: '#4f46e5', color: 'white', fontSize: '10px', fontWeight: '500', padding: '2px 6px', borderRadius: '4px' }}>AI-Powered</div>
                                </div>
                            </div>
                            
                            {/* REMOVED Risk Incidents Section Header Div */}
                            
                            {/* Risk Incidents Content - Moved h3 inside */}
                            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', marginBottom: '24px', padding: '24px' }}>
                                {/* MOVED and styled h3 */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.25px' }}>Risk Incidents</h3>
                                    {/* Filter button moved here */}
                                    <div>
                                        <button style={{
                                            padding: '8px 14px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            backgroundColor: '#f8fafc',
                                            fontSize: '13px',
                                            fontWeight: 500,
                                            color: '#334155',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}>
                                            <Filter size={14} /> Filter Incidents
                                        </button>
                                    </div>
                                </div>
                                
                                <div style={{ marginBottom: '24px' }}>
                                    <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: '#fef2f2', marginBottom: '12px', border: '1px solid #fee2e2' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <AlertCircle size={18} style={{ color: '#dc2626' }} />
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>Failed Authentication Attempts</span>
                                            </div>
                                            <span style={{ fontSize: '13px', color: '#64748b' }}>May 12, 2024</span>
                                        </div>
                                        <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#334155', lineHeight: '1.5' }}>
                                            Multiple failed login attempts (17) from unrecognized IP addresses in 24-hour period. Security team investigating potential credential stuffing attack.
                                        </p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                            <div>
                                                <span style={{ fontSize: '12px', fontWeight: 500, padding: '3px 8px', borderRadius: '4px', backgroundColor: '#fff1f2', color: '#be123c', marginRight: '8px' }}>
                                                    High Severity
                                                </span>
                                                <span style={{ fontSize: '12px', fontWeight: 500, padding: '3px 8px', borderRadius: '4px', backgroundColor: '#ffe4e6', color: '#e11d48' }}>
                                                    Active
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Clock size={14} />
                                                Opened: 36 hours ago
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: '#fffbeb', marginBottom: '12px', border: '1px solid #fef3c7' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <AlertCircle size={18} style={{ color: '#d97706' }} />
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>API Integration Issue</span>
                                            </div>
                                            <span style={{ fontSize: '13px', color: '#64748b' }}>April 28, 2024</span>
                                        </div>
                                        <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#334155', lineHeight: '1.5' }}>
                                            Intermittent webhook delivery failures following partner's API version upgrade. 23 failed callbacks requiring manual transaction reconciliation.
                                        </p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                            <div>
                                                <span style={{ fontSize: '12px', fontWeight: 500, padding: '3px 8px', borderRadius: '4px', backgroundColor: '#fef3c7', color: '#b45309', marginRight: '8px' }}>
                                                    Medium Severity
                                                </span>
                                                <span style={{ fontSize: '12px', fontWeight: 500, padding: '3px 8px', borderRadius: '4px', backgroundColor: '#f1f5f9', color: '#475569' }}>
                                                    Resolved
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#047857', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <CheckCircle size={14} />
                                                Resolved on May 2, 2024
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Historical Incidents Section */}
                                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '24px' }}>
                                    {/* Updated h4 style */}
                                    <h4 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', margin: '0 0 16px 0' }}>Historical Incidents</h4>
                                    
                                    {/* Incident Item */}
                                    <div style={{ 
                                        padding: '16px', 
                                        borderRadius: '8px', 
                                        backgroundColor: '#fef2f2', 
                                        marginBottom: '12px', 
                                        border: '1px solid #fee2e2' 
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <AlertCircle size={18} style={{ color: '#dc2626' }} />
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>Fraud Cluster Detection</span>
                                            </div>
                                            <span style={{ fontSize: '13px', color: '#64748b' }}>Mar 17, 2024</span>
                                        </div>
                                        <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#334155', lineHeight: '1.5' }}>
                                            Pattern of 23 suspicious transactions identified from same device fingerprints across multiple customer accounts. Total value: {partner.currency} 48,750.00
                                        </p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                            <div>
                                                <span style={{ 
                                                    fontSize: '12px', 
                                                    fontWeight: 500, 
                                                    padding: '3px 8px', 
                                                    borderRadius: '4px', 
                                                    backgroundColor: '#fff1f2', 
                                                    color: '#be123c',
                                                    marginRight: '8px'
                                                }}>Critical Severity</span>
                                                <span style={{ 
                                                    fontSize: '12px', 
                                                    fontWeight: 500, 
                                                    padding: '3px 8px', 
                                                    borderRadius: '4px', 
                                                    backgroundColor: '#f1f5f9', 
                                                    color: '#475569'
                                                }}>Resolved</span>
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#dc2626',
                                                fontWeight: '500',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M3 7V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V17M13 16L16 12M16 12L13 8M16 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                Financial Impact: {partner.currency} 36,420.00
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '10px', fontSize: '12px', color: '#64748b', padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px', lineHeight: '1.5' }}>
                                            <strong>Resolution:</strong> Coordinated response with partner's security team. Implemented enhanced device fingerprinting. Recovered 25% of funds through payment reversals. Updated fraud detection algorithms.
                                        </div>
                                    </div>
                                    
                                    {/* Incident Item 2 */}
                                    <div style={{ 
                                        padding: '16px', 
                                        borderRadius: '8px', 
                                        backgroundColor: '#fffbeb', 
                                        marginBottom: '12px', 
                                        border: '1px solid #fef3c7' 
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <AlertCircle size={18} style={{ color: '#d97706' }} />
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>System Integration Failure</span>
                                            </div>
                                            <span style={{ fontSize: '13px', color: '#64748b' }}>Jan 30, 2024</span>
                                        </div>
                                        <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#334155', lineHeight: '1.5' }}>
                                            Partner's API integration failed after their infrastructure migration. 78 transactions affected during 4-hour outage window. Error rate: 93%.
                                        </p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                            <div>
                                                <span style={{ 
                                                    fontSize: '12px', 
                                                    fontWeight: 500, 
                                                    padding: '3px 8px', 
                                                    borderRadius: '4px', 
                                                    backgroundColor: '#fef3c7', 
                                                    color: '#b45309',
                                                    marginRight: '8px'
                                                }}>Medium Severity</span>
                                                <span style={{ 
                                                    fontSize: '12px', 
                                                    fontWeight: 500, 
                                                    padding: '3px 8px', 
                                                    borderRadius: '4px', 
                                                    backgroundColor: '#f1f5f9', 
                                                    color: '#475569'
                                                }}>Resolved</span>
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#ea580c',
                                                fontWeight: '500',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M3 7V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V17M13 16L16 12M16 12L13 8M16 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                Financial Impact: {partner.currency} 3,250.00
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '10px', fontSize: '12px', color: '#64748b', padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px', lineHeight: '1.5' }}>
                                            <strong>Resolution:</strong> Implemented fallback processing mode. Engineering teams from both organizations collaborated on fix. Established pre-migration testing protocol for future updates.
                                        </div>
                                    </div>
                                    
                                    {/* Incident Item 3 */}
                                    <div style={{ 
                                        padding: '16px', 
                                        borderRadius: '8px', 
                                        backgroundColor: '#f0f9ff', 
                                        marginBottom: '12px', 
                                        border: '1px solid #e0f2fe' 
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <AlertCircle size={18} style={{ color: '#0284c7' }} />
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>Regulatory Compliance Update</span>
                                            </div>
                                            <span style={{ fontSize: '13px', color: '#64748b' }}>Dec 15, 2023</span>
                                        </div>
                                        <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#334155', lineHeight: '1.5' }}>
                                            New KYC requirements implemented in GCC region requiring additional verification steps. Partner needed to update customer onboarding flow.
                                        </p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                            <div>
                                                <span style={{ 
                                                    fontSize: '12px', 
                                                    fontWeight: 500, 
                                                    padding: '3px 8px', 
                                                    borderRadius: '4px', 
                                                    backgroundColor: '#e0f2fe', 
                                                    color: '#0369a1',
                                                    marginRight: '8px'
                                                }}>Low Severity</span>
                                                <span style={{ 
                                                    fontSize: '12px', 
                                                    fontWeight: 500, 
                                                    padding: '3px 8px', 
                                                    borderRadius: '4px', 
                                                    backgroundColor: '#f1f5f9', 
                                                    color: '#475569'
                                                }}>Resolved</span>
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#0284c7',
                                                fontWeight: '500',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                Compliance: Full
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '10px', fontSize: '12px', color: '#64748b', padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px', lineHeight: '1.5' }}>
                                            <strong>Resolution:</strong> Compliance team provided updated documentation requirements. Partner implemented changes within 30-day grace period. Verification process now includes enhanced identity checks.
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '24px' }}>
                                <button style={{ 
                                    flex: 1,
                                    padding: '12px 16px', 
                                    borderRadius: '8px', 
                                    border: 'none', 
                                    backgroundColor: '#f1f5f9', 
                                    fontSize: '14px', 
                                    fontWeight: 500, 
                                    color: '#334155', 
                                    cursor: 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    gap: '8px'
                                }}>
                                    <AlertCircle size={16} /> Report New Incident
                                </button>
                                <button style={{ 
                                    flex: 1,
                                    padding: '12px 16px', 
                                    borderRadius: '8px', 
                                    border: 'none', 
                                    backgroundColor: '#f1f5f9', 
                                    fontSize: '14px', 
                                    fontWeight: 500, 
                                    color: '#334155', 
                                    cursor: 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    gap: '8px'
                                }}>
                                    <Download size={16} /> Export Incident Report
                                </button>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: activeTab === 'Company Details' ? 'block' : 'none' }}>
                        {/* Legal Details Content */}
                        <div style={{ padding: '24px' }}>
                            <div style={{ 
                                backgroundColor: 'white', 
                                borderRadius: '12px', 
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                padding: '24px',
                                maxWidth: '700px' // Adjust width as needed
                            }}>
                                {/* Header */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.25px' }}>Legal details</h2>
                                        {/* Use StatusBadge component for 'Active' */}
                                        <StatusBadge status={partner.status} /> 
                                    </div>
                                    <button style={{ 
                                        padding: '8px', 
                                        borderRadius: '8px', 
                                        border: 'none', 
                                        backgroundColor: '#f8fafc', 
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)', 
                                        color: '#64748b', 
                                        cursor: 'pointer'
                                    }}>
                                        <MoreHorizontal size={18} />
                                    </button>
                                </div>
                                
                                {/* Legal Details Section */}
                                <CardSection title=""> {/* No title needed as per design */}
                                    <DetailRow label="Legal entity country" value={countryCodeToName[partner.location] || partner.location} />
                                    <DetailRow label="Legal company name" value={partner.name} />
                                    <DetailRow label="Legal address" value={partner.businessInfo.address} valueAlign="left" />
                                </CardSection>

                                {/* Business License Section */}
                                <CardSection title="Business license">
                                    <DetailRow label="Business license" value="Commercial Registration" />
                                    <DetailRow label="Commercial registration number" value={partner.businessInfo.registration} />
                                    <DetailRow 
                                        label="Upload your business license" 
                                        value={
                                            <button style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', padding: 0, fontWeight: 500 }}>
                                                <Download size={14} /> Download
                                            </button>
                                        } 
                                    />
                                    <DetailRow 
                                        label="National ID of the license holder" 
                                        value={
                                            <button style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', padding: 0, fontWeight: 500 }}>
                                                <Download size={14} /> Download
                                            </button>
                                        } 
                                    />
                                </CardSection>
                                
                                {/* Bank Details Section */}
                                <CardSection title="Bank details">
                                    <DetailRow label="IBAN" value={"VG50 TDN6 3804 0079 0011 1847"} /> {/* Placeholder */}
                                    <DetailRow label="Bank name" value={"JP Morgan"} /> {/* Placeholder */}
                                    <DetailRow label="Swift code" value={"943294"} /> {/* Placeholder */}
                                    <DetailRow label="Beneficiary name in English" value={"Tabby Storer"} /> {/* Placeholder */}
                                    <DetailRow label="Bank account currency" value={partner.currency} />
                                    <DetailRow label="Email addresses to receive settlement reports" value="-" />
                                </CardSection>

                                {/* VAT Section */}
                                <CardSection title="VAT">
                                    <DetailRow label="VAT registration number" value={partner.businessInfo.registration} /> {/* Using registration as per image */}
                                    <DetailRow label="Address as per Tax Certificate" value={partner.businessInfo.registration} /> {/* Using registration as per image - Address seems to be the CRN in the image? Clarify if needed. */}
                                    <DetailRow 
                                        label="VAT certificate" 
                                        value={
                                            <button style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', padding: 0, fontWeight: 500 }}>
                                                <Download size={14} /> Download
                                            </button>
                                        } 
                                    />
                                </CardSection>

                                {/* Removed old Contacts and Company Details cards */}
                                {/* 
                                // Contacts Card (OLD - REMOVED) 
                                // ... 
                                // Company Details Card (OLD - REMOVED) 
                                // ... 
                                */}
                            </div>

                            {/* Commercial Terms Card */}
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                padding: '24px',
                                marginTop: '28px', // Add spacing from the card above
                                maxWidth: '700px'
                            }}>
                                <h2 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.25px' }}>Commercial terms</h2>
                                <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#64748b' }}>These are the standard Tabby commercial terms.</p>
                                
                                <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', textAlign: 'center', padding: '16px 0', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                                    {/* Variable Fee */}
                                    <div style={{ padding: '0 16px' }}>
                                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Variable fee</div>
                                        <div style={{ fontSize: '18px', fontWeight: 500, color: '#111827' }}>6.99%</div> {/* Placeholder */}
                                    </div>
                                    {/* Divider */}
                                    <div style={{ width: '1px', height: '40px', backgroundColor: '#f1f5f9' }}></div>
                                    {/* Fixed Fee */}
                                    <div style={{ padding: '0 16px' }}>
                                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Fixed fee</div>
                                        <div style={{ fontSize: '18px', fontWeight: 500, color: '#111827' }}>{partner.currency} 1.00</div> {/* Placeholder, use partner currency */}
                                    </div>
                                    {/* Divider */}
                                    <div style={{ width: '1px', height: '40px', backgroundColor: '#f1f5f9' }}></div>
                                    {/* Total */}
                                    <div style={{ padding: '0 16px' }}>
                                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Total per transaction</div>
                                        <div style={{ fontSize: '18px', fontWeight: 500, color: '#111827' }}>6.99% + {partner.currency} 1.00</div> {/* Placeholder */}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Contract Card */}
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                padding: '24px',
                                marginTop: '28px',
                                maxWidth: '700px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div>
                                    <h2 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.25px' }}>Contract</h2>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>
                                        Download your signed contract or view the <br />
                                        copy we sent to <strong style={{ color: '#334155' }}>example@gmail.com</strong> {/* Placeholder email */}
                                    </p>
                                </div>
                                <button style={{ 
                                    padding: '10px 20px', 
                                    borderRadius: '20px', // Pill shape 
                                    border: 'none', 
                                    backgroundColor: '#f1f5f9', 
                                    fontSize: '14px', 
                                    fontWeight: 500, 
                                    color: '#334155', 
                                    cursor: 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <Download size={16} /> Download
                                </button>
                            </div>

                            {/* --- NEW WIDGETS START --- */}

                            {/* Locations Widget */}
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                padding: '24px',
                                marginTop: '28px',
                                maxWidth: '700px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.25px' }}>Locations</h2>
                                    <button style={{
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: '#111827', // Dark background for Add button
                                        color: 'white',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s ease'
                                    }}>
                                        Add
                                    </button>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', cursor: 'pointer' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            backgroundColor: '#e5e7eb', // Light gray circle background
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Send size={18} color="#6b7280" />
                                        </div>
                                        <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Connected locations</span>
                                        <span style={{
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            backgroundColor: '#e5e7eb', // Gray badge background
                                            color: '#374151',
                                            padding: '3px 8px',
                                            borderRadius: '12px'
                                        }}>
                                            69 {/* Mock count */}
                                        </span>
                                    </div>
                                    <ChevronRight size={20} color="#9ca3af" />
                                </div>
                            </div>

                            {/* Contacts Widget */}
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                padding: '24px',
                                marginTop: '28px',
                                maxWidth: '700px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.25px' }}>Contacts</h2>
                                    <button style={{
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: '#111827', // Dark background for Add button
                                        color: 'white',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s ease'
                                    }}>
                                        Add
                                    </button>
                                </div>
                                <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#64748b' }}>Invite new team members to help you run your business</p>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', cursor: 'pointer' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            backgroundColor: '#e5e7eb', // Light gray circle background
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Users size={18} color="#6b7280" />
                                        </div>
                                        <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Contacts</span>
                                        <span style={{
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            backgroundColor: '#e5e7eb', // Gray badge background
                                            color: '#374151',
                                            padding: '3px 8px',
                                            borderRadius: '12px'
                                        }}>
                                            7 {/* Mock count */}
                                        </span>
                                    </div>
                                    <ChevronRight size={20} color="#9ca3af" />
                                </div>
                            </div>

                            {/* --- NEW WIDGETS END --- */}

                         </div>
                     </div>
                 </div>
            </div>
        </MainLayout>
    );
};

export default PartnerDetailsPage;
